import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { inputCls, Field } from "./_shared";

export default function SettingsAdmin() {
  const [nextDate, setNextDate] = useState(""); // YYYY-MM-DD
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      if (!data.next_session_date) { setNextDate(""); return; }
      setNextDate(String(data.next_session_date).slice(0, 10));
    }).finally(() => setLoading(false));
  }, []);

  // Store the date as noon Luxembourg time. Countdown only shows whole days so
  // a 1-hour drift between CET and CEST is invisible.
  const dateToIso = (ymd) => (ymd ? `${ymd}T12:00:00+01:00` : null);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/settings", { next_session_date: dateToIso(nextDate) });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/settings", { next_session_date: null });
      setNextDate("");
      toast.success("Cleared");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to clear");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-white/50 font-mono text-sm uppercase tracking-widest py-16 text-center">Loading…</div>;

  const daysLeft = nextDate
    ? Math.max(0, Math.ceil((new Date(`${nextDate}T12:00:00+01:00`).getTime() - Date.now()) / 86400000))
    : null;

  return (
    <div data-testid="settings-admin" className="max-w-2xl">
      <div className="text-xs font-mono uppercase tracking-[0.3em] text-lime mb-2">Settings</div>
      <h1 className="font-heading text-4xl font-black uppercase tracking-tighter mb-10">Site controls</h1>

      <section className="bg-surface border border-white/10 p-6 md:p-8 space-y-5">
        <div>
          <div className="font-heading text-xl font-black uppercase tracking-tight mb-1">Next session date</div>
          <p className="text-white/50 text-sm">Powers the countdown on the landing page. Stored as noon Luxembourg time on the selected day. Leave empty to show "TBD".</p>
        </div>
        <Field label="Date (Luxembourg)">
          <Input
            data-testid="settings-next-date"
            type="date"
            value={nextDate}
            onChange={(e) => setNextDate(e.target.value)}
            className={inputCls}
          />
        </Field>
        {daysLeft !== null && (
          <div className="text-xs font-mono uppercase tracking-widest text-white/60" data-testid="settings-preview">
            Preview: <span className="text-lime font-bold tabular-nums">{daysLeft}</span> {daysLeft === 1 ? "day" : "days"} from now
          </div>
        )}
        <div className="flex flex-wrap gap-3 pt-2">
          <button
            data-testid="settings-save"
            onClick={save}
            disabled={saving}
            className="px-6 py-3 bg-lime text-black font-heading font-black uppercase tracking-wider hover:scale-[1.03] transition-transform disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save →"}
          </button>
          <button
            data-testid="settings-clear"
            onClick={clear}
            disabled={saving}
            className="px-6 py-3 border border-white/10 font-mono text-xs uppercase tracking-widest hover:border-danger hover:text-danger transition-colors disabled:opacity-50"
          >
            Clear (show TBD)
          </button>
        </div>
      </section>
    </div>
  );
}
